import { useEffect, useState } from "react";
import { clearAllWritingDrafts } from "@/lib/writing-draft-storage";
import { useNavigate } from "react-router-dom";
import { ChevronRight } from "lucide-react";
import { Capacitor } from "@capacitor/core";
import { Preferences } from "@capacitor/preferences";
import { supabase } from "@/integrations/supabase/client";
import { useUserSession } from "@/hooks/use-user-session";
import { enablePush, disablePush, getPushPermissionState } from "@/lib/push";

const NOTIF_KEY = "notifications_enabled";
const SUPPORT_EMAIL = "support@lurneax.com";
const APP_VERSION = "v1.0.0";

function initialsFrom(name?: string | null, email?: string | null) {
  if (name) {
    return name
      .split(" ")
      .filter(Boolean)
      .map((n) => n[0])
      .join("")
      .toUpperCase()
      .slice(0, 2);
  }
  return (email?.[0] || "U").toUpperCase();
}

async function prefGet(key: string): Promise<string | null> {
  if (Capacitor.isNativePlatform()) {
    const { value } = await Preferences.get({ key });
    return value;
  }
  return localStorage.getItem(key);
}
async function prefSet(key: string, value: string) {
  if (Capacitor.isNativePlatform()) {
    await Preferences.set({ key, value });
  } else {
    localStorage.setItem(key, value);
  }
}

export default function AccountScreen() {
  const navigate = useNavigate();
  const { data: session } = useUserSession();
  const [notifEnabled, setNotifEnabled] = useState(false);
  const [notifBusy, setNotifBusy] = useState(false);
  const [notifHint, setNotifHint] = useState<string | null>(null);
  const [signingOut, setSigningOut] = useState(false);

  const name = session?.fullName || null;
  const email = session?.email || null;
  const centre =
    (session?.locations?.[0]?.locations as any)?.name || null;
  const initials = initialsFrom(name, email);

  // Reflect real state on load: ON requires both the user pref AND the OS permission.
  useEffect(() => {
    let cancelled = false;
    (async () => {
      const stored = await prefGet(NOTIF_KEY);
      const pref = stored === null ? true : stored === "true";
      if (Capacitor.isNativePlatform()) {
        const state = await getPushPermissionState();
        if (cancelled) return;
        setNotifEnabled(state === "granted" && pref);
      } else {
        if (cancelled) return;
        setNotifEnabled(pref);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  const toggleNotif = async () => {
    if (notifBusy) return;
    setNotifHint(null);
    const next = !notifEnabled;
    setNotifBusy(true);
    try {
      if (next) {
        if (Capacitor.isNativePlatform()) {
          const result = await enablePush();
          if (result === "denied") {
            setNotifHint("Enable notifications in your device Settings.");
            setNotifEnabled(false);
            return;
          }
        }
        await prefSet(NOTIF_KEY, "true");
        setNotifEnabled(true);
      } else {
        await disablePush();
        await prefSet(NOTIF_KEY, "false");
        setNotifEnabled(false);
      }
    } finally {
      setNotifBusy(false);
    }
  };



  const handleSignOut = async () => {
    if (signingOut) return;
    setSigningOut(true);
    try {
      await clearAllWritingDrafts(); // explicit user sign-out only
      await supabase.auth.signOut();
    } catch {
      // ignore – fall through and clear local state
    }
    for (const key of Object.keys(localStorage)) {
      if (key.startsWith("sb-") || key.startsWith("supabase")) {
        localStorage.removeItem(key);
      }
    }
    navigate(Capacitor.isNativePlatform() ? "/app/login" : "/login", { replace: true });
  };

  return (
    <div className="min-h-screen bg-[#FBEAE0] px-5 pb-32 pt-12">
      <h1 className="text-2xl font-extrabold text-[#2A1810]">Account</h1>

      <div className="mt-5 flex items-center gap-4 rounded-2xl border border-[#F0DDD2] bg-[#FFF6EC] p-4">
        <span className="flex h-14 w-14 flex-none items-center justify-center rounded-full bg-[#F44321] text-base font-extrabold text-white">
          {initials}
        </span>
        <div className="min-w-0 flex-1">
          <p className="truncate text-sm font-extrabold text-[#2A1810]">{name || "User"}</p>
          {email && <p className="truncate text-xs font-semibold text-[#8A6050]">{email}</p>}
          {centre && <p className="truncate text-xs font-semibold text-[#8A6050]">{centre}</p>}
        </div>
      </div>
      <p className="mt-2 text-[11px] font-semibold text-[#8A6050]">Managed by your centre</p>

      <p className="mt-6 text-[11px] font-extrabold tracking-wider text-[#8A6050]">PREFERENCES</p>
      <div className="mt-2 flex items-center justify-between rounded-2xl border border-[#F0DDD2] bg-[#FFF6EC] px-4 py-3">
        <span className="text-sm font-bold text-[#2A1810]">Notifications</span>
        <button
          onClick={toggleNotif}
          disabled={notifBusy}
          role="switch"
          aria-checked={notifEnabled}
          className="relative h-[18px] w-[34px] rounded-full transition-colors disabled:opacity-60"
          style={{ background: notifEnabled ? "#F44321" : "#E0CFC4" }}
        >
          <span
            className="absolute top-0.5 h-[14px] w-[14px] rounded-full bg-white transition-all"
            style={{ left: notifEnabled ? 18 : 2 }}
          />
        </button>
      </div>
      {notifHint && (
        <p className="mt-2 text-[11px] font-semibold text-[#B8431D]">{notifHint}</p>
      )}

      <p className="mt-6 text-[11px] font-extrabold tracking-wider text-[#8A6050]">SUPPORT</p>
      <div className="mt-2 overflow-hidden rounded-2xl border border-[#F0DDD2] bg-[#FFF6EC]">
        <a
          href={`mailto:${SUPPORT_EMAIL}`}
          className="flex items-center justify-between px-4 py-3"
        >
          <span className="text-sm font-bold text-[#2A1810]">Help & support</span>
          <ChevronRight className="h-4 w-4 text-[#8A6050]" />
        </a>
        <div className="h-px bg-[#F0DDD2]" />
        <div className="flex items-center justify-between px-4 py-3">
          <span className="text-sm font-bold text-[#2A1810]">About</span>
          <span className="text-xs font-semibold text-[#8A6050]">{APP_VERSION}</span>
        </div>
      </div>

      <button
        onClick={handleSignOut}
        disabled={signingOut}
        className="mt-8 w-full rounded-2xl border border-[#F44321] bg-[#FFF6EC] py-3 text-sm font-extrabold text-[#F44321] disabled:opacity-60"
      >
        {signingOut ? "Signing out…" : "Sign out"}
      </button>
    </div>
  );
}
